import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

const ACTIVITY_KEY = 'srp_last_activity';

function isTokenExpired(token) {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!payload.exp) return false;
    return Date.now() >= payload.exp * 1000;
  } catch {
    return true;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('srp_user');
    const token = localStorage.getItem('srp_token');
    // Check token expiry on mount
    if (token && isTokenExpired(token)) {
      localStorage.removeItem('srp_token');
      localStorage.removeItem('srp_user');
      localStorage.removeItem(ACTIVITY_KEY);
      return null;
    }
    return stored ? JSON.parse(stored) : null;
  });
  const [token, setToken] = useState(() => {
    const t = localStorage.getItem('srp_token');
    if (t && isTokenExpired(t)) {
      localStorage.removeItem('srp_token');
      return null;
    }
    return t;
  });
  const [loading, setLoading] = useState(false);
  const [sessionTimeout, setSessionTimeout] = useState(15);
  const lastActivityRef = useRef(Date.now());
  const timerRef = useRef(null);

  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Fetch session timeout from settings
  useEffect(() => {
    if (token) {
      api.get('/api/settings').then(r => {
        if (r.data?.session_timeout_minutes) {
          setSessionTimeout(r.data.session_timeout_minutes);
        }
      }).catch(() => {});
    }
  }, [token]);

  const doLogout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('srp_token');
    localStorage.removeItem('srp_user');
    localStorage.removeItem(ACTIVITY_KEY);
    delete api.defaults.headers.common['Authorization'];
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  // On mount: check stored last activity time for stale sessions
  useEffect(() => {
    if (!token) return;

    const storedActivity = localStorage.getItem(ACTIVITY_KEY);
    if (storedActivity) {
      const elapsed = (Date.now() - parseInt(storedActivity, 10)) / 1000 / 60;
      if (elapsed >= sessionTimeout) {
        doLogout();
        window.location.href = '/login?expired=1';
        return;
      }
      lastActivityRef.current = parseInt(storedActivity, 10);
    } else {
      // First time: set now
      localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
      lastActivityRef.current = Date.now();
    }

    // Also validate token server-side on mount and refresh user data (permissions etc.)
    api.get('/api/auth/me').then((res) => {
      if (res.data) {
        setUser(res.data);
        localStorage.setItem('srp_user', JSON.stringify(res.data));
      }
    }).catch((err) => {
      if (err.response?.status === 401) {
        doLogout();
        window.location.href = '/login?expired=1';
      }
    });
  }, [token, sessionTimeout, doLogout]);

  // Activity tracking
  useEffect(() => {
    if (!token) return;
    const resetActivity = () => {
      const now = Date.now();
      lastActivityRef.current = now;
      localStorage.setItem(ACTIVITY_KEY, String(now));
    };
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetActivity));

    timerRef.current = setInterval(() => {
      // Check client-side token expiry
      if (isTokenExpired(token)) {
        doLogout();
        window.location.href = '/login?expired=1';
        return;
      }
      // Check inactivity
      const elapsed = (Date.now() - lastActivityRef.current) / 1000 / 60;
      if (elapsed >= sessionTimeout) {
        doLogout();
        window.location.href = '/login?expired=1';
      }
    }, 30000);

    return () => {
      events.forEach(e => window.removeEventListener(e, resetActivity));
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [token, sessionTimeout, doLogout]);

  const loginWithToken = useCallback((t, u) => {
    setToken(t);
    setUser(u);
    localStorage.setItem('srp_token', t);
    localStorage.setItem('srp_user', JSON.stringify(u));
    localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
    api.defaults.headers.common['Authorization'] = `Bearer ${t}`;
    lastActivityRef.current = Date.now();
  }, []);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const res = await api.post('/api/auth/login', { email, password });
      // 2FA flow: login returns requires_2fa instead of token
      if (res.data.requires_2fa) {
        return res.data; // { requires_2fa, login_token, masked_email, expires_in }
      }
      const { token: t, user: u } = res.data;
      loginWithToken(t, u);
      return { user: u };
    } finally {
      setLoading(false);
    }
  }, [loginWithToken]);

  const logout = doLogout;

  const lastRefreshRef = useRef(0);
  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get('/api/auth/me');
      setUser(res.data);
      localStorage.setItem('srp_user', JSON.stringify(res.data));
      lastRefreshRef.current = Date.now();
    } catch { /* 401 handled by interceptor */ }
  }, []);

  // Auto-refresh permissions on tab focus (throttled: at most once per 15s)
  useEffect(() => {
    if (!token) return;
    const THROTTLE_MS = 15000;
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastRefreshRef.current > THROTTLE_MS) {
        refreshUser();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    // Also poll every 60s to keep permissions fresh
    const pollId = setInterval(() => { refreshUser(); }, 60000);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      clearInterval(pollId);
    };
  }, [token, refreshUser]);

  return (
    <AuthContext.Provider value={{ user, token, login, loginWithToken, logout, refreshUser, loading, isAuthenticated: !!token }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  const hasPerm = (perm) => ctx.user?.permissions?.includes(perm) ?? false;
  return { ...ctx, hasPerm };
};
